/**
 * Web Vitals measurement utility
 *
 * This module provides utilities to measure Core Web Vitals:
 * - Largest Contentful Paint (LCP)
 * - Interaction to Next Paint (INP) - replaces FID in v5
 * - Cumulative Layout Shift (CLS)
 * - First Contentful Paint (FCP)
 * - Time to First Byte (TTFB)
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

type WebVitalsCallback = (metric: Metric) => void;

export const reportWebVitals = (onPerfEntry?: WebVitalsCallback): void => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    onCLS(onPerfEntry);
    onINP(onPerfEntry); // INP replaces FID in web-vitals v5
    onFCP(onPerfEntry);
    onLCP(onPerfEntry);
    onTTFB(onPerfEntry);
  }
};

// Analytics helper to send vitals to your preferred analytics service
export const sendToAnalytics = (metric: Metric): void => {
  // Example for Google Analytics 4
  if (typeof gtag !== 'undefined') {
    gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
    });
  }

  // Console logging for development
  if (process.env.NODE_ENV === 'development') {
    //console.log(`${metric.name}: ${metric.value}`, metric);
  }
};

// Thresholds for Core Web Vitals (Google's recommended values)
export const WEB_VITALS_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // milliseconds
  INP: { good: 200, needsImprovement: 500 }, // milliseconds (replaces FID)
  CLS: { good: 0.1, needsImprovement: 0.25 }, // score
  FCP: { good: 1800, needsImprovement: 3000 }, // milliseconds
  TTFB: { good: 800, needsImprovement: 1800 }, // milliseconds
} as const;

export const getVitalStatus = (metric: Metric): 'good' | 'needs-improvement' | 'poor' => {
  const threshold = WEB_VITALS_THRESHOLDS[metric.name as keyof typeof WEB_VITALS_THRESHOLDS];
  if (!threshold) return 'good';

  if (metric.value <= threshold.good) return 'good';
  if (metric.value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
};

declare global {
  function gtag(...args: unknown[]): void;
}
